import { isAxiosError } from 'axios';
import type { InspectionDto, InspectionShotKey } from '@carvia/ros-client-types';
import { damagesService } from '@/api/damages.service';
import { inspectionsService } from '@/api/inspections.service';
import { uploadService } from '@/api/upload.service';
import type { CreateDamageDto, Damage } from '@/entities/damage.entity';
import { queryClient } from '@/queries/query-client';
import { DAMAGES_QUERY_KEY } from '@/queries/use-damages';
import { addItemToQueriesData, updateItemOnQueriesData } from '@/utils/update-query-data';

export type EvidenceKind = 'photo' | 'video' | 'inspection-shot' | 'damage';

/** A Damage saved while the server was out of reach, with the id the device chose for it. */
export type UnsentDamage = CreateDamageDto & { id: string };

export type EvidenceQueueItem = {
  id: string;
  userId: string;
  /**
   * Set for Damage photos and videos once the Damage exists. Without it the item is a draft from an
   * open damage form: it uploads right away but waits for `linkDrafts` before it is attached.
   */
  damageId?: string;
  carId?: string;
  /** Set for Inspection shots; captureId ties the upload to the registered capture. */
  inspectionId?: string;
  shotKey?: InspectionShotKey;
  captureId?: string;
  /** Set for an Unsent damage; its damageId is the id it will have on the server. */
  damage?: UnsentDamage;
  kind: EvidenceKind;
  name: string;
  mimeType: string;
  blob: Blob;
  /** Set once the file is in storage, so a retry only repeats the attach and never uploads twice. */
  uploadedFileKey?: string;
  lastError: string | null;
  /** Failed attempts so far; each one doubles the wait before the next. */
  failedAttempts?: number;
  /** A failed item waits until then, so it never holds up the items behind it. */
  nextAttemptAt?: number;
  createdAt: number;
};

/** Outcome of "send now": still waiting, failed again (with the last error), or all sent. */
export type SendAttemptResult = { pending: number; failed: number; lastError: string | null };

export type EvidenceUploadStatus = 'uploading' | 'done' | 'error';

/** Upload state of a queued file: done once it is in storage, error after a failed attempt. */
export function getDraftStatus(item: EvidenceQueueItem): EvidenceUploadStatus {
  if (item.uploadedFileKey) return 'done';
  return item.lastError ? 'error' : 'uploading';
}

type Listener = (items: EvidenceQueueItem[]) => void;

const DB_NAME = 'ros-evidence-upload';
const MAX_RETRY_DELAY_MS = 5 * 60 * 1000;

/** 5s, 10s, 20s, … up to 5 minutes; an item is never given up. */
function getRetryDelayMs(failedAttempts: number): number {
  return Math.min(5000 * 2 ** (failedAttempts - 1), MAX_RETRY_DELAY_MS);
}
const STORE = 'items';
export const INSPECTIONS_QUERY_KEY = 'inspections';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        const store = req.result.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('userId', 'userId');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function waitForIdbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

class EvidenceUploadQueue {
  private userId: string | null = null;
  private listeners = new Set<Listener>();
  private items: EvidenceQueueItem[] = [];
  private pumping = false;
  private wakeUp: (() => void) | null = null;
  private started = false;

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.pump();
    });
    window.addEventListener('online', () => void this.sendUnsentDamagesNow());
    await this.reload();
    await this.discardOrphanedDrafts();
    void this.pump();
  }

  setUser(userId: string | null): void {
    this.userId = userId;
    void this.reload().then(() => {
      if (userId) void this.pump();
    });
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.findVisibleItems());
    return () => this.listeners.delete(listener);
  }

  findVisibleItems(): EvidenceQueueItem[] {
    if (!this.userId) return [];
    return this.items.filter((item) => item.userId === this.userId);
  }

  async enqueue(
    damageId: string,
    carId: string,
    files: { kind: 'photo' | 'video'; file: Blob; name: string }[],
  ): Promise<void> {
    await this.put(
      files.map((file) => ({
        ...this.createItem(file.kind, file.file, file.name),
        damageId,
        carId,
      })),
    );
  }

  /**
   * Stores a confirmed Inspection shot on the device and uploads it. Older queued photos of the
   * same shot are dropped, since the server would reject them as superseded anyway.
   */
  async enqueueInspectionShot(shot: {
    inspectionId: string;
    shotKey: InspectionShotKey;
    captureId: string;
    file: Blob;
    name: string;
  }): Promise<void> {
    const superseded = this.items.filter(
      (item) =>
        item.kind === 'inspection-shot' &&
        item.inspectionId === shot.inspectionId &&
        item.shotKey === shot.shotKey,
    );
    for (const item of superseded) await this.remove(item.id);
    await this.put([
      {
        ...this.createItem('inspection-shot', shot.file, shot.name),
        inspectionId: shot.inspectionId,
        shotKey: shot.shotKey,
        captureId: shot.captureId,
      },
    ]);
  }

  /**
   * Keeps a Damage the server could not be reached for on the device, across restarts, and sends
   * it before any other item. Its evidence, linked by the same id, is attached once it arrived.
   */
  async enqueueUnsentDamage(damage: UnsentDamage): Promise<void> {
    const file = new Blob([JSON.stringify(damage)], { type: 'application/json' });
    await this.put([
      {
        ...this.createItem('damage', file, `damage-${damage.id}.json`),
        damage,
        damageId: damage.id,
        carId: damage.carId,
      },
    ]);
  }

  findUnsentDamages(): UnsentDamage[] {
    return this.findVisibleItems().flatMap((item) => (item.damage ? [item.damage] : []));
  }

  /** Drops an Unsent damage together with its evidence; it never reaches the server. */
  async discardUnsentDamage(damageId: string): Promise<void> {
    if (!this.isUnsentDamage(damageId)) return;
    for (const item of this.items.filter((row) => row.damageId === damageId)) {
      await this.remove(item.id);
    }
  }

  /**
   * Tries the Unsent damages right away instead of after their back-off, and resolves once each
   * was sent or failed again — or after `timeoutMs`, when an upload ahead of them is still running.
   */
  async sendUnsentDamagesNow(timeoutMs = 35_000): Promise<SendAttemptResult> {
    const attempted = this.findVisibleItems().filter((row) => row.kind === 'damage');
    if (!attempted.length) return { pending: 0, failed: 0, lastError: null };
    const attemptsBefore = new Map(attempted.map((item) => [item.id, item.failedAttempts ?? 0]));
    for (const item of attempted) {
      item.lastError = null;
      item.nextAttemptAt = undefined;
    }
    const findResult = (): SendAttemptResult | null => {
      const remaining = attempted.filter((item) => this.items.includes(item));
      const failed = remaining.filter(
        (item) => (item.failedAttempts ?? 0) > attemptsBefore.get(item.id)!,
      );
      if (failed.length < remaining.length) return null;
      return { pending: 0, failed: failed.length, lastError: failed[0]?.lastError ?? null };
    };
    return new Promise((resolve) => {
      let unsubscribe = () => {};
      const timeout = setTimeout(() => {
        unsubscribe();
        const remaining = attempted.filter((item) => this.items.includes(item));
        resolve({ pending: remaining.length, failed: 0, lastError: null });
      }, timeoutMs);
      unsubscribe = this.subscribe(() => {
        const result = findResult();
        if (!result) return;
        clearTimeout(timeout);
        unsubscribe();
        resolve(result);
      });
      void this.pump();
    });
  }

  /** Queues a photo or video from an open damage form; it starts uploading before the Damage exists. */
  async enqueueDraft(carId: string, kind: 'photo' | 'video', file: Blob, name: string) {
    const item = { ...this.createItem(kind, file, name), carId };
    await this.put([item]);
    return item.id;
  }

  /** Storage keys of drafts that finished uploading, so the Damage can be created with them. */
  findUploadedDraftKeys(ids: string[]): Map<string, string> {
    const keys = new Map<string, string>();
    for (const item of this.items) {
      if (ids.includes(item.id) && item.uploadedFileKey) keys.set(item.id, item.uploadedFileKey);
    }
    return keys;
  }

  /**
   * Ties drafts to the saved Damage. Drafts whose key went into the create request are done; the
   * rest are attached as soon as their upload finishes, including any that finished meanwhile.
   */
  async linkDrafts(ids: string[], damageId: string, attachedIds: string[]): Promise<void> {
    for (const id of attachedIds) await this.remove(id);
    for (const item of this.items.filter((row) => ids.includes(row.id) && !row.damageId)) {
      item.damageId = damageId;
      await this.persist(item);
    }
    this.emit();
    await this.pump();
  }

  /** Drops drafts of a damage form that was cancelled or whose photo was removed. */
  async discardDrafts(ids: string[]): Promise<void> {
    for (const id of ids) {
      if (this.items.some((item) => item.id === id && !item.damageId)) await this.remove(id);
    }
  }

  async retry(id?: string): Promise<void> {
    if (id) {
      const item = this.items.find((row) => row.id === id);
      if (item) {
        item.lastError = null;
        item.nextAttemptAt = undefined;
      }
      this.emit();
    }
    await this.pump();
  }

  async exportItem(id: string): Promise<File | null> {
    const item = this.items.find((row) => row.id === id && row.userId === this.userId);
    if (!item) return null;
    return new File([item.blob], item.name, { type: item.mimeType });
  }

  /** Items belong to the signed-in agent; only they upload or export them. */
  private createItem(kind: EvidenceKind, file: Blob, name: string): EvidenceQueueItem {
    if (!this.userId) throw new Error('No signed-in agent for the evidence upload queue');
    return {
      id: crypto.randomUUID(),
      userId: this.userId,
      kind,
      name,
      mimeType: file.type || (kind === 'video' ? 'video/webm' : 'image/jpeg'),
      blob: file,
      lastError: null,
      createdAt: Date.now(),
    };
  }

  private async put(items: EvidenceQueueItem[]): Promise<void> {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    for (const item of items) {
      store.put(item);
      this.items.push(item);
    }
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    this.emit();
    void this.pump();
  }

  private emit(): void {
    const snapshot = this.findVisibleItems();
    for (const listener of this.listeners) listener(snapshot);
  }

  private async reload(): Promise<void> {
    const db = await openDb();
    const rows = await waitForIdbRequest(db.transaction(STORE).objectStore(STORE).getAll());
    this.items = (rows as EvidenceQueueItem[]) ?? [];
    this.emit();
  }

  private async pump(): Promise<void> {
    if (this.pumping) {
      // A retry, a new item or coming back online ends the wait for a backed-off item.
      this.wakeUp?.();
      return;
    }
    if (!this.userId || document.visibilityState === 'hidden') return;
    this.pumping = true;
    try {
      while (this.userId) {
        const mine = this.findVisibleItems().filter((item) => !this.isParkedDraft(item));
        if (!mine.length) break;
        const now = Date.now();
        const next = mine
          .filter((item) => (item.nextAttemptAt ?? 0) <= now)
          .sort(
            (a, b) =>
              Number(b.kind === 'damage') - Number(a.kind === 'damage') ||
              a.createdAt - b.createdAt,
          )[0];
        if (!next) {
          const wakeAt = Math.min(...mine.map((item) => item.nextAttemptAt ?? now));
          await new Promise<void>((resolve) => {
            this.wakeUp = resolve;
            setTimeout(resolve, wakeAt - now);
          });
          this.wakeUp = null;
          continue;
        }
        try {
          if (await this.uploadOne(next)) await this.remove(next.id);
          else this.emit();
        } catch (error) {
          next.lastError = error instanceof Error ? error.message : 'Upload failed';
          next.failedAttempts = (next.failedAttempts ?? 0) + 1;
          next.nextAttemptAt = Date.now() + getRetryDelayMs(next.failedAttempts);
          await this.persist(next);
          this.emit();
        }
      }
    } finally {
      this.pumping = false;
    }
  }

  /** Uploads and attaches an item; false when it is a draft that stays parked until linked. */
  private async uploadOne(item: EvidenceQueueItem): Promise<boolean> {
    if (item.kind === 'inspection-shot') {
      await this.uploadInspectionShot(item);
      return true;
    }
    if (item.damage) {
      const damage = await damagesService.create(item.damage);
      addItemToQueriesData<Damage>([DAMAGES_QUERY_KEY], damage, queryClient);
      return true;
    }
    const fileKey = await this.uploadOnce(item, async () => {
      const { fileKey: key } = await uploadService.uploadDamageMedia(
        { carId: item.carId!, originalFileName: item.name },
        item.blob,
      );
      return key;
    });
    if (!item.damageId || this.isUnsentDamage(item.damageId)) return false;
    const damage = await damagesService.attachEvidence(item.damageId, {
      imageFileKey: item.kind === 'photo' ? fileKey : undefined,
      videoFileKey: item.kind === 'video' ? fileKey : undefined,
    });
    updateItemOnQueriesData<Damage>([DAMAGES_QUERY_KEY], damage, queryClient);
    return true;
  }

  /**
   * Uploaded drafts, and evidence of an Unsent damage, wait for their Damage; they must not hold
   * the queue busy meanwhile.
   */
  private isParkedDraft(item: EvidenceQueueItem): boolean {
    if (item.kind === 'inspection-shot' || item.kind === 'damage' || !item.uploadedFileKey) {
      return false;
    }
    return !item.damageId || this.isUnsentDamage(item.damageId);
  }

  private isUnsentDamage(damageId: string): boolean {
    return this.items.some((item) => item.kind === 'damage' && item.damageId === damageId);
  }

  /** Drafts survive only as long as the damage form that made them, which a restart closes. */
  private async discardOrphanedDrafts(): Promise<void> {
    const orphaned = this.items.filter((item) => item.kind !== 'inspection-shot' && !item.damageId);
    for (const item of orphaned) await this.remove(item.id);
  }

  private async uploadInspectionShot(item: EvidenceQueueItem): Promise<void> {
    const inspectionId = item.inspectionId!;
    const fileKey = await this.uploadOnce(
      item,
      async () => (await inspectionsService.uploadImage(inspectionId, item.blob)).fileKey,
    );
    try {
      const inspection = await inspectionsService.attachImage(
        inspectionId,
        item.shotKey!,
        item.captureId!,
        fileKey,
      );
      updateItemOnQueriesData<InspectionDto>([INSPECTIONS_QUERY_KEY], inspection, queryClient);
    } catch (error) {
      // 409: a retake superseded this capture, or its photo already landed — nothing left to do.
      if (isAxiosError(error) && error.response?.status === 409) return;
      throw error;
    }
  }

  /** Uploads the file unless an earlier attempt already did, and remembers the storage key. */
  private async uploadOnce(
    item: EvidenceQueueItem,
    upload: () => Promise<string>,
  ): Promise<string> {
    if (item.uploadedFileKey) return item.uploadedFileKey;
    item.uploadedFileKey = await upload();
    // A draft discarded while it was uploading must not be written back.
    if (this.items.includes(item)) await this.persist(item);
    return item.uploadedFileKey;
  }

  private async persist(item: EvidenceQueueItem): Promise<void> {
    const db = await openDb();
    await waitForIdbRequest(db.transaction(STORE, 'readwrite').objectStore(STORE).put(item));
  }

  private async remove(id: string): Promise<void> {
    const db = await openDb();
    await waitForIdbRequest(db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id));
    this.items = this.items.filter((item) => item.id !== id);
    this.emit();
  }
}

export const evidenceUploadQueue = new EvidenceUploadQueue();
